import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AcademyQuestion } from '../../academy/academy.service';

export interface AcademyQuizResult {
  correct: number;
  total: number;
  /** Anteil richtiger Antworten in Prozent (Übung: beim ersten Versuch). */
  percent: number;
}

interface QuestionState {
  picked: number | null;
  /** Übung: falsch gewählte Antworten — bleiben rot, bis die richtige kommt. */
  wrong: Set<number>;
  firstTry: boolean | null;
}

/**
 * Quiz der Academy, zwei Modi:
 *  - `practice` (Mini-Quiz einer Lektion): sofortiges Feedback je Klick.
 *    Falsch → rot, nochmal versuchen (die richtige Antwort wird nicht
 *    verraten); richtig → grün plus Erklärung. `completed` feuert, sobald
 *    jede Frage richtig beantwortet ist.
 *  - `exam` (Abschlusstest): auswählen, dann „Auswerten“ — erst danach
 *    zeigt jede Frage richtig/falsch. `completed` trägt das Ergebnis.
 */
@Component({
  selector: 'app-academy-quiz',
  templateUrl: './academy-quiz.component.html',
  styleUrls: ['./academy-quiz.component.scss'],
})
export class AcademyQuizComponent implements OnChanges {
  @Input({ required: true }) questions: AcademyQuestion[] = [];
  @Input() mode: 'practice' | 'exam' = 'practice';
  @Output() completed = new EventEmitter<AcademyQuizResult>();

  state: QuestionState[] = [];
  submitted = false;
  private emitted = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['questions']) this.reset();
  }

  reset(): void {
    this.state = this.questions.map(() => ({ picked: null, wrong: new Set<number>(), firstTry: null }));
    this.submitted = false;
    this.emitted = false;
  }

  pick(qi: number, oi: number): void {
    const q = this.questions[qi];
    const s = this.state[qi];
    if (this.mode === 'exam') {
      if (!this.submitted) s.picked = oi;
      return;
    }
    if (s.picked === q.correct) return; // schon gelöst
    if (s.firstTry === null) s.firstTry = oi === q.correct;
    if (oi === q.correct) {
      s.picked = oi;
      this.maybeComplete();
    } else {
      s.wrong.add(oi);
    }
  }

  /** Zustand einer Antwort für die Darstellung. */
  optionState(qi: number, oi: number): 'idle' | 'selected' | 'correct' | 'wrong' {
    const q = this.questions[qi];
    const s = this.state[qi];
    if (this.mode === 'practice') {
      if (s.picked === oi) return 'correct';
      return s.wrong.has(oi) ? 'wrong' : 'idle';
    }
    if (!this.submitted) return s.picked === oi ? 'selected' : 'idle';
    if (oi === q.correct) return 'correct';
    return s.picked === oi ? 'wrong' : 'idle';
  }

  solved(qi: number): boolean {
    return this.state[qi]?.picked === this.questions[qi]?.correct;
  }

  /** Übung: zuletzt falsch geklickt, noch nicht gelöst. */
  retry(qi: number): boolean {
    return this.mode === 'practice' && !this.solved(qi) && (this.state[qi]?.wrong.size ?? 0) > 0;
  }

  get allPicked(): boolean {
    return this.state.every((s) => s.picked !== null);
  }

  get answeredCount(): number {
    return this.state.filter((s) => s.picked !== null).length;
  }

  submit(): void {
    if (this.submitted) return;
    this.submitted = true;
    const correct = this.questions.filter((q, i) => this.state[i].picked === q.correct).length;
    this.emit(correct);
  }

  private maybeComplete(): void {
    if (!this.questions.every((_, i) => this.solved(i))) return;
    const correct = this.state.filter((s) => s.firstTry).length;
    this.emit(correct);
  }

  private emit(correct: number): void {
    if (this.emitted) return;
    this.emitted = true;
    const total = this.questions.length;
    this.completed.emit({ correct, total, percent: total ? Math.round((correct / total) * 100) : 0 });
  }

  trackQuestion(_: number, q: AcademyQuestion): string {
    return q.key;
  }
}
